/**
 * Maintenance Mode Component
 *
 * Sistem bakım modunda olduğunda gösterilen tam ekran overlay.
 * Kullanıcılara bakım mesajını gösterir ve tüm işlemleri engeller.
 */

import React from 'react';
import { DotPattern } from '../layout/DotPattern';
import { Wrench, Instagram, Clock, AlertCircle } from 'lucide-react';

interface MaintenanceModeProps {
  message: string;
}

export const MaintenanceMode: React.FC<MaintenanceModeProps> = ({ message }) => {
  return (
    <div className="min-h-screen w-full z-50 flex items-center justify-center bg-slate-50 relative overflow-hidden">
      {/* Background Pattern */}
      <DotPattern
        className="opacity-40"
        dotColor="#0ea5e9" // sky-500
      />

      {/* Dynamic Background Gradients */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl -translate-y-1/2 animate-pulse-slow" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-indigo-500/10 rounded-full blur-3xl translate-y-1/2 animate-pulse-slow delay-1000" />
      </div>

      {/* Main Card */}
      <div className="relative w-full max-w-lg mx-4">
        <div className="bg-white/80 backdrop-blur-2xl rounded-3xl p-8 md:p-12 shadow-2xl border border-white/50 text-center relative overflow-hidden group hover:shadow-blue-200/50 transition-all duration-500">

          {/* Top Decorative Line */}
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-500 via-indigo-500 to-blue-500 opacity-50" />

          {/* Icon Area */}
          <div className="relative mx-auto w-24 h-24 mb-8">
            <div className="absolute inset-0 bg-blue-100 rounded-full animate-ping opacity-20" />
            <div className="relative w-full h-full bg-gradient-to-br from-blue-50 to-indigo-50 rounded-full flex items-center justify-center border border-indigo-100 shadow-inner">
              <Wrench className="w-10 h-10 text-blue-600 animate-spin-slow-reverse" />
              <div className="absolute -bottom-1 -right-1 bg-white p-1.5 rounded-full shadow-sm border border-slate-100">
                <AlertCircle className="w-5 h-5 text-amber-500" />
              </div>
            </div>
          </div>

          {/* Text Content */}
          <h1 className="text-3xl font-bold text-slate-900 mb-4 tracking-tight">
            Sistem Bakımda
          </h1>

          <div className="flex items-center justify-center gap-2 text-slate-500 mb-6 text-sm font-medium">
            <Clock className="w-4 h-4" />
            <span>Tahmini Süre: 1 Saat</span>
          </div>

          <p className="text-slate-600 text-lg leading-relaxed mb-8">
            {message || 'Sistem şu anda bakım aşamasındadır. Daha iyi bir deneyim sunmak için çalışıyoruz.'}
          </p>

          {/* Action Area */}
          <div className="space-y-4">
            <a
              href="https://instagram.com/arizarehberimcom"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-3 w-full py-4 px-6 bg-gradient-to-r from-purple-600 via-pink-600 to-red-600 text-white rounded-xl font-semibold shadow-lg shadow-pink-200 hover:shadow-pink-300 transform hover:-translate-y-0.5 transition-all duration-300 group/btn"
            >
              <Instagram className="w-5 h-5 group-hover/btn:scale-110 transition-transform" />
              <span>Instagram'da Takip Et</span>
            </a>

            <p className="text-xs text-slate-400 font-medium">
              Güncel durum ve duyurular için bizi takip edin
            </p>
          </div>

        </div>

        {/* Footer Brand */}
        <div className="mt-8 text-center">
          <p className="text-slate-400 font-medium text-sm flex items-center justify-center gap-2">
            <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
            ArızaRehberim Ekibi
          </p>
        </div>
      </div>
    </div>
  );
};
